import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AiAdapter, AiSendPayload } from './adapter';
import { AiMessage, AiModelRef } from '../models';
import { environment } from '../../../../environments/environment';

export interface CodeSuggestionRequest {
	code: string;
	language: string;
	suggestionType?: string;
	context?: string;
	customPrompt?: string;
	includeExplanation?: boolean;
	modelId?: string;
}

export interface CodeSuggestionResponse {
	suggestedCode: string;
	explanation?: string;
	changes: string[];
	hadErrors: boolean;
	errors: string[];
	tokensUsed?: number;
	model: string;
}

/** Frame image for video interpolation (first/last frame) */
export interface FrameImage {
	imageUrl?: string;
	imageBase64?: string;
}

export interface VideoGenerationRequest {
	prompt: string;
	durationSeconds?: number;
	aspectRatio?: '16:9' | '9:16';
	resolution?: '720p' | '1080p';
	negativePrompt?: string;
	personGeneration?: 'dont_allow'; // Note: 'allow_adult' is not currently supported by VEO API
	seed?: number;
	/** Style/Subject reference images for content guidance */
	referenceImages?: { imageUrl?: string; imageBase64?: string; referenceType: 'style' | 'subject' }[];
	/** First frame image for video interpolation */
	firstFrame?: FrameImage;
	/** Last frame image for video interpolation */
	lastFrame?: FrameImage;
}

export interface VideoGenerationStatus {
	status: 'pending' | 'processing' | 'completed' | 'failed';
	operationId?: string;
	progress?: number;
	videoUrl?: string;
	thumbnailUrl?: string;
	durationSeconds?: number;
	error?: string;
}

@Injectable({ providedIn: 'root' })
export class AiGatewayService implements AiAdapter {
  constructor(private http: HttpClient) {}

  send(req: AiSendPayload): Observable<AiMessage> {
    return this.http.post<AiMessage>(`${environment.apiUrl}/ai/send`, {
      prompt: req.prompt,
      modelId: req.model?.id,
      mode: req.mode,
      contextTags: req.context,
      responseTypes: req.responseTypes,
      params: req.params
    });
  }

  cancel(_requestId: string): void {}

  models(): Observable<AiModelRef[]> {
    return this.http.get<AiModelRef[]>(`${environment.apiUrl}/ai/models`);
  }


  getCodeSuggestions(request: CodeSuggestionRequest): Observable<CodeSuggestionResponse> {
    return new Observable<CodeSuggestionResponse>(subscriber => {
      // First, POST to get streamId
      this.http.post<{ streamId: string }>(
        `${environment.apiUrl}/ai/code-suggestions`,
        request
      ).subscribe({
        next: (response) => {
          const streamId = response.streamId;
          
          // Connect to EventSource with the streamId
          const eventSource = new EventSource(
            `${environment.apiUrl}/ai/code-suggestions/${streamId}`
          );

          eventSource.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              if(data.type === 'ping') {
                // Ignore ping messages
                return;
              }

              if(data.suggestedCode) {
                subscriber.next(JSON.parse(event.data) as CodeSuggestionResponse);
                // We aren't actually streaming messages here,
                // so close after the first one.
                eventSource.close();
                subscriber.complete();
              }
            } catch (error) {
              subscriber.error(new Error('Failed to parse SSE data'));
            }
          };

          eventSource.onerror = (error) => {
            eventSource.close();
            subscriber.error(error);
          };

          // Cleanup on unsubscribe
          return () => {
            eventSource.close();
          };
        },
        error: (error) => {
          subscriber.error(error);
        }
      });
    });
  }

  /**
   * Start video generation using VEO 3.1
   * Returns a pending status with operation ID for polling
   */
  generateVideo(request: VideoGenerationRequest): Observable<VideoGenerationStatus> {
    return this.http.post<VideoGenerationStatus>(
      `${environment.apiUrl}/ai/video/generate`,
      request
    );
  }

  /**
   * Check status of a video generation operation
   * Poll this until status is 'completed' or 'failed'
   */
  checkVideoStatus(operationId: string): Observable<VideoGenerationStatus> {
    return this.http.get<VideoGenerationStatus>(
      `${environment.apiUrl}/ai/video/status/${encodeURIComponent(operationId)}`
    );
  }
}
